import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authClient } from "../lib/auth-client";
import { apiFetch, IS_PREVIEW_ENVIRONMENT } from "../lib/api-client";
import { isFieldApp } from "../lib/field-app";

type Membership = {
  isOwner: boolean;
} | null;

type MeResponse = {
  data: {
    applicationUser: Membership;
  };
};

export type RootLayoutContext = {
  onRemoveCompany: () => void;
  isRemovingCompany: boolean;
  removeCompanyError: string;
};

export default function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending } = authClient.useSession();
  const user = session?.user;
  const [membership, setMembership] = useState<Membership | undefined>(undefined);
  const [isRemovingCompany, setIsRemovingCompany] = useState(false);
  const [removeCompanyError, setRemoveCompanyError] = useState("");

  useEffect(() => {
    if (!user) {
      setMembership(undefined);
      setRemoveCompanyError("");
      return;
    }

    let cancelled = false;
    void apiFetch("/api/v1/me")
      .then(async (response) => {
        if (!response.ok) throw new Error("Unable to load workspace assignment");
        return (await response.json()) as MeResponse;
      })
      .then((response) => {
        if (!cancelled) setMembership(response.data.applicationUser);
      })
      .catch(() => {
        if (!cancelled) setMembership(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  async function handleSignOut() {
    await authClient.signOut();
    navigate("/");
  }

  async function handleRemoveCompany() {
    if (!user) return;

    const confirmation = membership
      ? "Remove this company? This permanently deletes its onboarding progress, company data, jobs, agent conversations, and local chat history. Your login will remain."
      : "Remove this unfinished company setup? This permanently clears its onboarding progress, agent conversations, and local chat history. Your login will remain.";
    if (!window.confirm(confirmation)) return;

    setRemoveCompanyError("");
    setIsRemovingCompany(true);
    try {
      const response = await apiFetch("/api/v1/companies/reset", { method: "POST" });
      if (!response.ok) {
        setRemoveCompanyError("We couldn’t remove this company setup. Please try again.");
        return;
      }

      window.location.assign("/onboarding");
    } catch {
      setRemoveCompanyError("We couldn’t reach the service. Please try again.");
    } finally {
      setIsRemovingCompany(false);
    }
  }

  const isAuth = location.pathname.startsWith("/auth");
  const isWorkspace = location.pathname === "/dashboard" || location.pathname.startsWith("/jobs/") || location.pathname === "/mycompany";
  const isTechnician = location.pathname.startsWith("/technician") || isFieldApp(undefined, location.search);
  return (
    /*
     * The footer sits outside .app-shell on purpose. Every rule in the shell
     * normalisation block is a descendant selector, so a signed-out page would
     * repaint the footer black the moment it saw a `text-` class on it.
     */
    <div className="flex min-h-screen flex-col bg-white">
    {IS_PREVIEW_ENVIRONMENT && <div className="bg-amber-100 px-4 py-2 text-center text-xs font-semibold text-amber-950" role="status">Stripe Sandbox — no real charges. Preview data and test payments can be replaced during the next refresh.</div>}
    <div className={`app-shell ${isWorkspace ? "workspace-shell" : ""} ${isTechnician ? "technician-shell" : ""} flex flex-1 flex-col bg-white text-slate-900`}>
      {!isWorkspace && !isTechnician && <header className="border-b border-slate-200/80 bg-white/90 px-6 py-4 backdrop-blur sm:px-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="Workview Pro home">
            <img src="/workview-pro-logo.png" alt="WorkView Pro" width={4800} height={805} className="h-auto w-44 sm:w-60" />
          </Link>

          {!isPending && user ? (
            <div className="flex items-center gap-3 text-sm">
              <span className="hidden text-slate-600 sm:inline">{user.name || user.email}</span>
              <button
                type="button"
                onClick={handleSignOut}
                className="rounded-lg border border-slate-200 px-3 py-2 font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Sign out
              </button>
            </div>
          ) : !isPending ? (
            <Link
              to={isAuth ? (new URLSearchParams(location.search).get("mode") === "sign-up" ? "/auth" : "/auth?mode=sign-up") : "/auth"}
              className="rounded-lg bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--accent)]"
            >
              {isAuth && new URLSearchParams(location.search).get("mode") !== "sign-up" ? "Sign up" : "Log in"}
            </Link>
          ) : null}
        </div>
      </header>}
      <div className="flex-1"><Outlet context={{ onRemoveCompany: () => void handleRemoveCompany(), isRemovingCompany, removeCompanyError } satisfies RootLayoutContext} /></div>
    </div>
    <footer className="px-6 py-5 text-center text-xs text-[var(--ink-faint)] sm:px-10">
      Powered by <span className="font-semibold text-[var(--ink-soft)]">Workview Pro</span>
    </footer>
    </div>
  );
}
